import { useState, useCallback } from 'react';
import type { HttpError } from '@refinedev/core';

/**
 * Тип для ошибок по полям формы
 * Ключ - имя поля, значение - массив сообщений об ошибках
 */
export type FieldErrors = Record<string, string[]>;

/**
 * Результат хука useServerErrors
 */
export interface UseServerErrorsResult {
  /** Ошибки по полям формы */
  fieldErrors: FieldErrors;
  /** Получить первую ошибку для поля */
  getFieldError: (fieldName: string) => string | undefined;
  /** Получить все ошибки для поля */
  getFieldErrors: (fieldName: string) => string[];
  /** Проверить, есть ли ошибка у поля */
  hasFieldError: (fieldName: string) => boolean;
  /** Установить ошибки из HttpError */
  setErrorsFromResponse: (error: HttpError | Error | unknown) => void;
  /** Очистить ошибки для конкретного поля */
  clearFieldError: (fieldName: string) => void;
  /** Очистить все ошибки */
  clearAllErrors: () => void;
  /** Общее сообщение об ошибке (если не привязано к полю) */
  generalError: string | undefined;
  /** Есть ли какие-либо ошибки */
  hasErrors: boolean;
}

/**
 * Хук для обработки ошибок валидации от сервера
 *
 * Извлекает ошибки из HttpError и предоставляет удобный API
 * для отображения ошибок на полях формы.
 *
 * @example
 * ```tsx
 * const { fieldErrors, getFieldError, setErrorsFromResponse, clearFieldError } = useServerErrors();
 *
 * // В onError callback:
 * onError: (error) => {
 *   setErrorsFromResponse(error);
 * }
 *
 * // В компоненте:
 * <FormField
 *   label="Email"
 *   name="email"
 *   error={getFieldError('email')}
 * >
 *   <Input
 *     value={email}
 *     onChange={(e) => {
 *       setEmail(e.target.value);
 *       clearFieldError('email');
 *     }}
 *   />
 * </FormField>
 * ```
 */
export function useServerErrors(): UseServerErrorsResult {
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [generalError, setGeneralError] = useState<string | undefined>();

  /**
   * Извлечь ошибки из HttpError
   */
  const setErrorsFromResponse = useCallback((error: HttpError | Error | unknown) => {
    if (!error) {
      setFieldErrors({});
      setGeneralError(undefined);
      return;
    }

    // HttpError от Refine/data-provider
    if (typeof error === 'object' && error !== null && 'errors' in error) {
      const httpError = error as HttpError;

      if (httpError.errors && typeof httpError.errors === 'object') {
        // Нормализуем ошибки - могут приходить как string[] или string
        const normalized: FieldErrors = {};

        for (const [field, messages] of Object.entries(httpError.errors)) {
          if (Array.isArray(messages)) {
            normalized[field] = messages.map(m => String(m));
          } else if (typeof messages === 'string') {
            normalized[field] = [messages];
          } else if (messages && typeof messages === 'object' && 'message' in messages) {
            // Формат { key: string, message: string }
            normalized[field] = [(messages as { message: string }).message];
          }
        }

        setFieldErrors(normalized);

        // Если есть общая ошибка (не привязанная к полю)
        if (normalized.general) {
          setGeneralError(normalized.general[0]);
        } else if (httpError.message && !Object.keys(normalized).length) {
          setGeneralError(httpError.message);
        } else {
          setGeneralError(undefined);
        }

        return;
      }

      // Если нет errors, но есть message
      if (httpError.message) {
        setGeneralError(httpError.message);
        setFieldErrors({});
        return;
      }
    }

    // Обычная Error
    if (error instanceof Error) {
      setGeneralError(error.message);
      setFieldErrors({});
      return;
    }

    // Неизвестный тип
    setGeneralError('Произошла неизвестная ошибка');
    setFieldErrors({});
  }, []);

  /**
   * Получить первую ошибку для поля
   */
  const getFieldError = useCallback((fieldName: string): string | undefined => {
    const errors = fieldErrors[fieldName];
    if (!errors || errors.length === 0) {
      return undefined;
    }
    // Убираем префикс "fieldName: " если он есть
    const firstError = errors[0];
    const prefix = `${fieldName}: `;
    if (firstError.startsWith(prefix)) {
      return firstError.slice(prefix.length);
    }
    return firstError;
  }, [fieldErrors]);

  /**
   * Получить все ошибки для поля
   */
  const getFieldErrors = useCallback((fieldName: string): string[] => {
    const errors = fieldErrors[fieldName] || [];
    // Убираем префикс "fieldName: " если он есть
    return errors.map(error => {
      const prefix = `${fieldName}: `;
      if (error.startsWith(prefix)) {
        return error.slice(prefix.length);
      }
      return error;
    });
  }, [fieldErrors]);

  /**
   * Проверить, есть ли ошибка у поля
   */
  const hasFieldError = useCallback((fieldName: string): boolean => {
    const errors = fieldErrors[fieldName];
    return !!errors && errors.length > 0;
  }, [fieldErrors]);

  /**
   * Очистить ошибку для конкретного поля
   */
  const clearFieldError = useCallback((fieldName: string) => {
    setFieldErrors(prev => {
      if (!(fieldName in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);

  /**
   * Очистить все ошибки
   */
  const clearAllErrors = useCallback(() => {
    setFieldErrors({});
    setGeneralError(undefined);
  }, []);

  return {
    fieldErrors,
    getFieldError,
    getFieldErrors,
    hasFieldError,
    setErrorsFromResponse,
    clearFieldError,
    clearAllErrors,
    generalError,
    hasErrors: Object.keys(fieldErrors).length > 0 || !!generalError,
  };
}
